import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Divider, FormControlLabel, MenuItem, Paper,
  Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import DifferenceIcon from '@mui/icons-material/Difference';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SearchIcon from '@mui/icons-material/Search';

import api from '../../../../services/api';
import TabelaDeHosts from './TabelaDeHosts';
import DiffTexto from './DiffTexto';
import RawLadoALado from './RawLadoALado';
import CompareEventos from './CompareEventos';
import { CODIGO, SUPERFICIE, fmtData } from './estilo';

const ROTA = '/admin/snapshot';

/**
 * Comparacao de duas coletas, em quatro formatos.
 *
 * UM par de seletores comanda tudo. Antes havia um quadro para "marcar duas e
 * comparar" e outro, com selecao independente, para o conteudo lado a lado —
 * duas formas de escolher a mesma coisa. E o resultado estruturado ia parar no
 * rodape da pagina, fora da vista de quem tinha acabado de clicar.
 *
 * Os quatro formatos respondem perguntas diferentes sobre o MESMO par:
 *   resumo      — o que mudou de verdade na rede (ignora campo volatil)
 *   lado a lado — como estava de cada lado
 *   diff        — tudo que mudou no texto, ruido incluido
 *   json        — o dado estruturado, para depurar parser ou colar num chamado
 */
/**
 * Seletor de coleta e painel de conteudo, no NIVEL DO MODULO.
 *
 * Estavam declarados dentro do VisualizadorColetas. Componente declarado dentro
 * de outro ganha identidade nova a cada render do pai, e o React trata isso como
 * um TIPO diferente: em vez de atualizar, ele desmonta a arvore inteira e monta
 * outra do zero.
 *
 * O sintoma nao parecia de React. O no sob o cursor era destruido e recriado, o
 * navegador reavaliava o :hover a cada troca e o ponteiro piscava; a rolagem
 * voltava ao topo; e o contador de "form field sem id" do DevTools subia sem
 * parar, porque cada remontagem criava um <input> novo do Select.
 *
 * Fora do corpo do pai, a identidade e estavel e o React reconcilia.
 */
function Seletor({ valor, aoMudar, cor, titulo, coletas }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
      <Chip size="small" label={titulo}
        sx={{ height: 20, bgcolor: cor, color: '#fff', fontWeight: 600 }} />
      <Select
        size="small" value={valor ?? ''} onChange={(e) => aoMudar(e.target.value)}
        sx={{ flex: 1, fontSize: 13 }}
      >
        {coletas.map((x) => (
          <MenuItem key={x.run_id} value={x.run_id} sx={{ fontSize: 13 }}>
            coleta {x.numero} · {fmtData(x.created_at)}
            {x.failed_hosts?.length ? `  (−${x.failed_hosts.length})` : ''}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}

const Painel = React.forwardRef(function Painel(
  { coleta, aoRolar, hostsPorId, empresa, abertos, onAlternar, busca }, ref
) {
  return (
    <Box
      ref={ref}
      onScroll={aoRolar}
      sx={{
        flex: 1, minWidth: 0, height: '58vh', overflow: 'auto',
        border: '1px solid #e2e8f0', borderRadius: 1, p: 1,
      }}
    >
      {coleta ? (
        <TabelaDeHosts
          coleta={coleta} hostsPorId={hostsPorId} empresa={empresa} compacto
          abertos={abertos} onAlternar={onAlternar} busca={busca}
        />
      ) : <Typography variant="caption" sx={{ color: '#94a3b8' }}>—</Typography>}
    </Box>
  );
});

export default function VisualizadorColetas({ coletas, hostsPorId, empresa, aoTrocarPar }) {
  const [esquerda, setEsquerda] = useState(null);
  const [direita, setDireita] = useState(null);
  const [sincronizar, setSincronizar] = useState(true);
  const [ladoRaw, setLadoRaw] = useState(false);
  const [formato, setFormato] = useState('resumo');
  const [busca, setBusca] = useState('');

  const [comparacao, setComparacao] = useState(null);
  const [comparando, setComparando] = useState(false);

  const [abertos, setAbertos] = useState(() => new Set());
  const refEsq = useRef(null);
  const refDir = useRef(null);
  // Uma marca de eco por painel, nao uma trava global — ver `espelhar`.
  const ecoEsq = useRef(false);
  const ecoDir = useRef(false);

  const porId = useMemo(
    () => Object.fromEntries(coletas.map((c) => [c.run_id, c])),
    [coletas]
  );

  const rotulo = useCallback(
    (runId) => (porId[runId] ? `coleta ${porId[runId].numero}` : `#${runId}`),
    [porId]
  );

  // `coletas` vem da mais recente para a mais antiga.
  useEffect(() => {
    if (!coletas.length) return;
    if (direita == null) setDireita(coletas[0].run_id);
    if (esquerda == null) {
      setEsquerda(coletas.length > 1 ? coletas[1].run_id : coletas[0].run_id);
    }
  }, [coletas, esquerda, direita]);

  // A comparacao roda SOZINHA quando o par muda: o botao "Comparar" era um passo
  // a mais para algo que a tela ja tinha tudo para fazer.
  useEffect(() => {
    if (!esquerda || !direita || esquerda === direita || !empresa) {
      setComparacao(null);
      return undefined;
    }
    const [a, b] = [esquerda, direita].slice().sort((x, y) => x - y);
    let cancelado = false;
    setComparando(true);
    api.get(`${ROTA}/run/compare/${a}/${b}`, { params: { empresa } })
      .then((res) => { if (!cancelado) setComparacao(res.data?.data || null); })
      .catch(() => { if (!cancelado) setComparacao(null); })
      .finally(() => { if (!cancelado) setComparando(false); });
    return () => { cancelado = true; };
  }, [esquerda, direita, empresa]);

  // Mantem a URL em dia: F5 e link colavel num chamado.
  useEffect(() => {
    if (esquerda && direita && esquerda !== direita) {
      const [a, b] = [esquerda, direita].slice().sort((x, y) => x - y);
      aoTrocarPar?.(a, b);
    }
  }, [esquerda, direita, aoTrocarPar]);

  const alternarHost = (hostId) => {
    setAbertos((prev) => {
      const nova = new Set(prev);
      if (nova.has(hostId)) nova.delete(hostId);
      else nova.add(hostId);
      return nova;
    });
  };

  /**
   * Rolagem sincronizada. Proporcional e nao absoluta — os dois lados quase
   * nunca tem a mesma altura.
   *
   * Escrever `scrollTop` no painel espelhado dispara o onScroll DELE, que
   * devolveria o movimento num laco. A versao anterior barrava isso com uma
   * trava unica: enquanto ela estava levantada, TODO evento de rolagem era
   * descartado — inclusive os novos, do painel que o operador esta arrastando.
   * Como scroll dispara muito mais rapido que um quadro, boa parte do movimento
   * era jogada fora e o lado espelhado andava aos trancos.
   *
   * Aqui a trava e por PAINEL e vale para um evento so: marca-se o painel de
   * destino como "o proximo onScroll dele e eco meu". A origem nunca e bloqueada,
   * entao o arrasto continua fluido.
   */
  const espelhar = useCallback(
    (origem, destino, ecoOrigem, ecoDestino) => () => {
      if (!sincronizar) return;

      // Este evento e o eco da escrita que EU fiz: consome a marca e para aqui.
      if (ecoOrigem.current) {
        ecoOrigem.current = false;
        return;
      }

      const a = origem.current;
      const b = destino.current;
      if (!a || !b) return;

      const max = a.scrollHeight - a.clientHeight;
      const frac = max > 0 ? a.scrollTop / max : 0;
      const alvo = frac * (b.scrollHeight - b.clientHeight);

      // Sem movimento real nao ha eco para engolir. Marcar assim mesmo deixaria
      // a trava pendurada e comeria o proximo arrasto legitimo daquele painel.
      if (Math.abs(b.scrollTop - alvo) < 1) return;

      ecoDestino.current = true;
      b.scrollTop = alvo;
      // Rede de seguranca: se o eco nao chegar (o navegador pode juntar eventos),
      // a marca cai sozinha no quadro seguinte em vez de ficar presa.
      requestAnimationFrame(() => { ecoDestino.current = false; });
    },
    [sincronizar]
  );

  // Handlers estaveis. Chamar `espelhar(...)` no meio do JSX criava uma funcao
  // nova a cada render, e o React trocava o listener de scroll junto.
  const aoRolarEsq = useMemo(
    () => espelhar(refEsq, refDir, ecoEsq, ecoDir), [espelhar]
  );
  const aoRolarDir = useMemo(
    () => espelhar(refDir, refEsq, ecoDir, ecoEsq), [espelhar]
  );

  const trocar = () => { setEsquerda(direita); setDireita(esquerda); };

  const jsonComparacao = useMemo(() => {
    if (!comparacao) return '';
    const texto = JSON.stringify(comparacao, null, 2);
    const termo = busca.trim().toLowerCase();
    if (!termo) return texto;
    return texto.split('\n').filter((l) => l.toLowerCase().includes(termo)).join('\n');
  }, [comparacao, busca]);

  if (!coletas.length) return null;

  const parValido = esquerda && direita && esquerda !== direita;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, ...SUPERFICIE }}>
      {/* Um par só, para todos os formatos. */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Seletor valor={esquerda} aoMudar={setEsquerda} cor="#64748b" titulo="antes" coletas={coletas} />
        <Tooltip title="Trocar os lados">
          <Chip
            icon={<SwapHorizIcon sx={{ fontSize: 17 }} />} label="" size="small"
            onClick={trocar}
            sx={{ cursor: 'pointer', '& .MuiChip-label': { display: 'none' } }}
          />
        </Tooltip>
        <Seletor valor={direita} aoMudar={setDireita} cor="#2563eb" titulo="depois" coletas={coletas} />
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup size="small" exclusive value={formato}
          onChange={(_, v) => { if (v) setFormato(v); }}>
          <ToggleButton value="resumo">
            <SummarizeIcon sx={{ fontSize: 16, mr: 0.5 }} /> resumo
          </ToggleButton>
          <ToggleButton value="lado">
            <VerticalSplitIcon sx={{ fontSize: 16, mr: 0.5 }} /> lado a lado
          </ToggleButton>
          <ToggleButton value="diff">
            <DifferenceIcon sx={{ fontSize: 16, mr: 0.5 }} /> diff de texto
          </ToggleButton>
          <ToggleButton value="json">
            <DataObjectIcon sx={{ fontSize: 16, mr: 0.5 }} /> JSON
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small" placeholder="buscar" sx={{ width: 200 }}
          value={busca} onChange={(e) => setBusca(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 17, mr: 0.5, color: '#94a3b8' }} />,
          }}
        />

        <Box sx={{ flex: 1 }} />

        {formato === 'lado' && (
          <>
            {/* Duas leituras do mesmo par: o que o parser entendeu, ou o texto
                que o equipamento devolveu. A segunda e a unica possivel quando o
                comando nao tem parser. */}
            <ToggleButtonGroup size="small" exclusive value={ladoRaw ? 'raw' : 'parse'}
              onChange={(_, v) => { if (v) setLadoRaw(v === 'raw'); }}>
              <ToggleButton value="parse">parseado</ToggleButton>
              <ToggleButton value="raw">texto cru</ToggleButton>
            </ToggleButtonGroup>

            {!ladoRaw && (
              <FormControlLabel
                control={
                  <Switch size="small" checked={sincronizar}
                    onChange={(e) => setSincronizar(e.target.checked)} />
                }
                label={<Typography variant="caption">rolagem e expand sincronizados</Typography>}
              />
            )}
          </>
        )}
      </Stack>

      {coletas.length === 1 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Só há uma coleta nesta sessão. Colete de novo depois de mexer na rede
          para ter os dois lados.
        </Alert>
      )}

      {!parValido && coletas.length > 1 && (
        <Alert severity="info">Escolha duas coletas diferentes.</Alert>
      )}

      {parValido && (
        <>
          {formato === 'resumo' && (
            comparando
              ? <Box sx={{ py: 2 }}><CircularProgress size={20} /></Box>
              : (
                <CompareEventos
                  dados={comparacao} hostsPorId={hostsPorId} busca={busca}
                  rotuloAntes={rotulo(esquerda)} rotuloDepois={rotulo(direita)}
                />
              )
          )}

          {formato === 'lado' && (
            ladoRaw ? (
              <RawLadoALado
                antes={Math.min(esquerda, direita)}
                depois={Math.max(esquerda, direita)}
                empresa={empresa}
              />
            ) : (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <Painel
                    ref={refEsq} coleta={porId[esquerda]} aoRolar={aoRolarEsq}
                    hostsPorId={hostsPorId} empresa={empresa} busca={busca}
                    abertos={sincronizar ? abertos : undefined}
                    onAlternar={sincronizar ? alternarHost : undefined}
                  />
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                <Painel
                    ref={refDir} coleta={porId[direita]} aoRolar={aoRolarDir}
                    hostsPorId={hostsPorId} empresa={empresa} busca={busca}
                    abertos={sincronizar ? abertos : undefined}
                    onAlternar={sincronizar ? alternarHost : undefined}
                  />
              </Stack>
            )
          )}

          {formato === 'diff' && (
            <DiffTexto
              antes={esquerda} depois={direita} empresa={empresa}
              rotuloAntes={rotulo(esquerda)} rotuloDepois={rotulo(direita)}
            />
          )}

          {formato === 'json' && (
            <Box sx={{ ...CODIGO, maxHeight: '58vh', overflow: 'auto', whiteSpace: 'pre' }}>
              {jsonComparacao || (comparando ? 'carregando…' : '—')}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
